<template>
    <div class="component component__library">
        <div class="component__library_filters">
            <base-filter
                ref="componentlibrarybasefilter"
                :overlay="false"
                :filter-data="items"
                :search-active="filterSearchActive"
                :search-placeholder="filterSearchPlaceholder"
                :search-queryable="filterSearchQueryable"
                :sort-active="filterSortActive"
                :filter-page-size="pageSize"
                :list-active="false"
                :checkbox-active="allowMultiple"
                :details-active="true"
                :details-items-number="items.length"
                :details-items-number-of="rawPagination ? (items.length > paginationTotalResults ? items.length : paginationTotalResults) : items.length"
                :details-items-name="itemName"
                :actions-active="filterActionsActive"
                :actions-secondary-button-active="false"
                :actions-primary-button-active="false"
                :actions-custom-button-active="Boolean(selected.length)"
                :actions-custom-button-icon="true"
                :actions-custom-button-icon-name="'trash-2'"
                :actions-custom-button-theme="'secondary-dark'"
                :actions-custom-notify-active="true"
                :actions-custom-notify-title="$t('lms.mediaLibrary.componentLibrary.deleteMedia')"
                :actions-custom-notify-submit-text="$t('lms.mediaLibrary.componentLibrary.delete')"
                :actions-custom-notify-text="$t('lms.mediaLibrary.componentLibrary.areYouSure')"
                :actions-custom-notify-prevent-close="true"
                :actions-custom-notify-submit-danger="true"
                :actions-custom-notify-submit-animate-loading="true"
                @emit-actions-custom-notify-submit="handleRemoveMedia"
                @emit-filter-result="setFilterResult"
                @emit-filter-query="setFilterQuery"
                @emit-filter-page-size="setFilterPageSize"
                @emit-checkbox="setSelectMultiple"
            />
        </div>
        <div class="component__library_main">
            <template v-if="rawItems === ''">
                <component-loading/>
            </template>
            <template v-else>
                <template v-if="items.length">
                    <component-library-grid
                        :items="items"
                        :checkbox-active="selectMultiple"
                        :selected="selectMultiple ? [] : selected"
                        @emit-check="handleEmitCheck"
                        @emit-click="handleEmitClick"
                    />
                </template>
                <template v-else>
                    <component-empty-state
                        :item="itemName.constructor === Array ? itemName[0] : itemName"
                        :title="query ? $t('lms.mediaLibrary.componentLibrary.noItems') : 'Uploaded media to your organisation will appear here.'"
                        :content="query ? $t('lms.mediaLibrary.componentLibrary.pleaseModify') : ''"
                        :submit-button-active="false"
                    />
                </template>
            </template>
        </div>
        <div class="component__library_pagination">
            <template v-if="getPaginationGetter && paginationHasPages">
                <component-pagination
                    :pagination="rawPagination"
                    @emit-page="setFilterPage"
                />
            </template>
        </div>
        <div class="component__library_uploader">
            <component-system-uploader-image
                ref="uploader"
                :formats="formats"
                @emit-upload="handleUploadMedia"
                @emit-upload-error="handleUploadMediaError"
                @emit-upload-success="handleUploadMediaSuccess"
            />
        </div>
    </div>
</template>

<script>
import BaseFilter from '@/components/default/filters/BaseFilter'
import ComponentPagination from '@/components/default/shared/ComponentPagination'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import ComponentEmptyState from '@/components/default/shared/ComponentEmptyState'
import ComponentLibraryGrid from '@/components/default/shared/ComponentLibraryGrid'
import ComponentSystemUploaderImage from '@/components/default/shared/ComponentSystemUploaderImage'

export default {
  name: 'component-library',
  components: { ComponentSystemUploaderImage, ComponentLibraryGrid, ComponentEmptyState, ComponentLoading, ComponentPagination, BaseFilter },
  data () {
    return {
      selected: [],
      selectMultiple: false,
      preloaded: [],
      loading: true,
      // data parameters for request
      page: 1,
      pageSize: 24,
      query: '',
      sort: '',
      sortBy: ''
    }
  },
  computed: {
    rawItems () {
      return this.$store.getters[this.getItemsGetter]
    },
    rawPagination () {
      return this.$store.getters[this.getPaginationGetter]
    },
    dispatchParams () {
      return this.page + this.pageSize + this.query
    },
    paginationHasPages () {
      return this.rawPagination ? this.rawPagination.pageSize < this.rawPagination.totalResults : undefined
    },
    paginationTotalResults () {
      return this.rawPagination ? this.rawPagination.totalResults : undefined
    },
    items () {
      return [...this.preloaded, ...this.rawItems]
    }
  },
  props: {
    formats: {
      type: String
    },
    allowMultiple: {
      type: Boolean,
      default: true
    },
    onlySelect: {
      type: Boolean,
      default: false
    },
    filterActionsActive: {
      type: Boolean,
      default: true
    },
    filterSearchActive: {
      type: Boolean,
      default: true
    },
    filterSearchQueryable: {
      type: Boolean,
      default: true
    },
    filterSearchPlaceholder: {
      type: String,
      default: 'Search media'
    },
    filterSortActive: {
      type: Boolean
    },
    getPaginationGetter: {
      type: String,
      default: 'getStorageFilesPagination'
    },
    getItemsGetter: {
      type: String,
      default: 'getStorageFiles'
    },
    getItemsDispatch: {
      type: String,
      default: 'storageFilesGet'
    },
    removeItemsDispatch: {
      type: String,
      default: 'storageFilesRemove'
    },
    itemName: {
      type: [String, Array],
      default: 'file'
    },
    customerId: {
      type: String
    }
  },
  methods: {
    getItems () {
      const page = this.page
      const pageSize = this.pageSize
      const query = encodeURIComponent(this.query)
      const sort = this.sort
      const sortBy = this.sortBy

      let customerId = this.customerId
      let queryUrlParams
      queryUrlParams = (page || pageSize || query || sort || sortBy) ? '?' : ''
      queryUrlParams += page ? `page=${page}` : ''
      queryUrlParams += pageSize && page ? '&' : ''
      queryUrlParams += pageSize ? `pageSize=${pageSize}` : ''
      queryUrlParams += query && (page || pageSize) ? '&' : ''
      queryUrlParams += query ? `filter=${query}` : ''
      queryUrlParams += sort && (page || pageSize || query) ? '&' : ''
      queryUrlParams += sort ? `sort=${sort}` : ''
      queryUrlParams += sortBy && (page || pageSize || query || sort) ? '&' : ''
      queryUrlParams += sortBy ? `sortBy=${sortBy}` : ''

      this.preloaded = []
      this.selected = []
      this.$store.dispatch(this.getItemsDispatch, { queryUrlParams, customerId })
    },
    handleUploadMedia (data) {
      this.preloaded = [...data, ...this.preloaded]
    },
    handleUploadMediaError (data) {
      let overridePreloaded = JSON.parse(JSON.stringify(this.preloaded))
      overridePreloaded.reverse()
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < overridePreloaded.length; j++) {
          if (overridePreloaded[j].title === data[i].title) {
            overridePreloaded[j].loading = false
            overridePreloaded[j].error = true
            continue
          }
        }
      }
      overridePreloaded.reverse()
      this.preloaded = overridePreloaded
    },
    handleUploadMediaSuccess (data) {
      let overridePreloaded = JSON.parse(JSON.stringify(this.preloaded))
      overridePreloaded.reverse()
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < overridePreloaded.length; j++) {
          if (overridePreloaded[j].title === data[i].title) {
            overridePreloaded[j] = data[i]
            continue
          }
        }
      }
      overridePreloaded.reverse()
      this.preloaded = overridePreloaded
    },
    handleRemoveMedia () {
      this.$store.dispatch(this.removeItemsDispatch, this.selected).then(() => {
        this.getItems()
      }).finally(() => {
        const baseFilterPopNotify = this.$refs.componentlibrarybasefilter.$refs.basefiltercustombuttonactive
        if (baseFilterPopNotify) {
          baseFilterPopNotify.closeNotify()
        }
      })
    },
    handleEmitCheck (data) {
      if (!this.selected.includes(data.id)) {
        if (!data.value) return
        this.selected.push(data.id)
      } else {
        if (data.value) return
        const itemIndex = this.selected.findIndex(item => {
          return item === data.id
        })
        this.selected.splice(itemIndex, 1)
      }
    },
    handleEmitClick (data) {
      if (this.onlySelect) {
        this.selected = this.selected.includes(data) ? [] : [data]
      } else {
        this.$router.push(({ name: 'media-library-edit', params: { id: data } }))
      }
    },
    openFileUploader () {
      this.$refs.uploader.openFileUploader()
    },
    setFilterResult (data) {
      this.outputItems = data
    },
    setFilterQuery (value) {
      this.setFilterPage(1)
      this.query = value
    },
    setFilterPageSize (value) {
      this.pageSize = value
    },
    setFilterPage (value) {
      this.page = value
    },
    setSelectMultiple (value) {
      this.selectMultiple = value
      this.selected = []
    }
  },
  watch: {
    dispatchParams () {
      this.getItems()
      this.preloaded = []
      this.$emit('emit-dispatch')
    }
  },
  created () {
    if (this.getItemsDispatch) {
      this.getItems()
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/shared/_componentlibrary.scss";
</style>
